import React, { useEffect, createElement } from 'react';
import { ArrowRightIcon, ZapIcon, SmartphoneIcon, SearchIcon, ShieldCheckIcon, CalendarIcon, MonitorIcon, CodeIcon } from 'lucide-react';
import { FinalCtaSection } from '../components/FinalCtaSection';
export function WebsiteTipsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Website tips | Verbeter je website | Noord';
    let metaDesc = document.querySelector('meta[name="description"]') as HTMLMetaElement;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.name = 'description';
      document.head.appendChild(metaDesc);
    }
    metaDesc.content = 'Praktische website tips voor ondernemers. Verbeter je conversie, snelheid en vindbaarheid met deze bewezen tips. Door de webdesigners van Noord Digital.';
    // Open Graph meta tags
    const ogTags: Record<string, string> = {
      'og:title': 'Website tips | Verbeter je website | Noord',
      'og:description': 'Praktische website tips voor ondernemers. Verbeter je conversie, snelheid en vindbaarheid met deze bewezen tips.',
      'og:type': 'article',
      'og:url': 'https://noorddigital.nl/website-tips',
      'og:site_name': 'Noord Digital',
      'og:locale': 'nl_NL'
    };
    Object.entries(ogTags).forEach(([property, content]) => {
      let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('property', property);
        document.head.appendChild(tag);
      }
      tag.content = content;
    });
  }, []);
  return <main className="pt-16 lg:pt-[72px]" data-id="element-2733" data-mp="3tlf8g3rong56">
      {/* Hero */}
      <section className="relative py-20 lg:py-32 bg-navy-900 overflow-hidden" data-id="element-2734" data-mp="2fx9gvaosgpn">
        <div className="absolute inset-0 pointer-events-none" data-id="element-2735" data-mp="1zaa6d3w8vbry">
          <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full opacity-20 blur-3xl bg-accent" data-id="element-2736" data-mp="1zbuxskn9iell" />
          <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full opacity-10 blur-3xl bg-accent-light" data-id="element-2737" data-mp="37j7pxyy1an7u" />
          <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px'
        }} data-id="element-2738" data-mp="32lfwv1lnkv22" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2739" data-mp="3qhjx5f5dxn0s">
          {/* Breadcrumb */}
          <nav className="flex items-center justify-center gap-2 text-sm mb-8 animate-fade-in-up" data-id="element-2740" data-mp="3li7e75hmx03h">
            <a href="#/" className="text-navy-400 hover:text-white transition-colors" data-id="element-2741" data-mp="347wycd8goj5">
              Home
            </a>
            <span className="text-navy-500" data-id="element-2742" data-mp="d374frm9ie4k">/</span>
            <a href="#/kennisbank" className="text-navy-400 hover:text-white transition-colors" data-id="element-2743" data-mp="d3ynk2yoajdy">
              Kennisbank
            </a>
            <span className="text-navy-500" data-id="element-2744" data-mp="rr783udvg12l">/</span>
            <span className="text-accent-light font-medium" data-id="element-2745" data-mp="rob4pqj98k6r">Website tips</span>
          </nav>
          <a href="#/kennisbank" className="inline-flex items-center gap-2 text-accent-light font-semibold text-sm tracking-widest uppercase mb-4 hover:text-white transition-colors animate-fade-in-up" data-id="element-2746" data-mp="wmwae5h9zw2l">
            ← Kennisbank
          </a>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white mb-6 animate-fade-in-up-delay-1" data-id="element-2747" data-mp="8yd0xmmhpd8x">
            Website tips voor bedrijven
          </h1>
          <p className="text-lg lg:text-xl text-navy-200 max-w-2xl mx-auto animate-fade-in-up-delay-2" data-id="element-2748" data-mp="3v7xewbha2i5c">
            Praktische tips voor betere websites. Verbeter je online
            aanwezigheid en trek meer klanten aan.
          </p>
        </div>
      </section>

      {/* Tip 1 — Snelheid */}
      <section className="py-24 bg-surface-muted" data-id="element-2749" data-mp="3q6q74cf4xiyo">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2750" data-mp="3q9m5u2yt4bo6">
          <div className="flex items-start gap-6" data-id="element-2751" data-mp="3j41v3d4cqxby">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2752" data-mp="3e5s135ud62rx">
              <ZapIcon className="w-7 h-7 text-accent" data-id="element-2753" data-mp="39e06mk837cwi" />
            </div>
            <div data-id="element-2754" data-mp="34fovm6622wcp">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2755" data-mp="34g8g9un3byuf">
                Tip 1
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2756" data-mp="30foj4r2iov8a">
                Zorg dat je website snel laadt
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2757" data-mp="2qfnt6ma57gv4">
                Snelheid is een van de belangrijkste factoren voor een
                succesvolle website. Bezoekers verwachten dat een pagina binnen
                2 seconden laadt. Duurt het langer? Dan haakt meer dan 50% af
                voordat ze je content hebben gezien.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2758" data-mp="jndb5z20v54f">
                Google gebruikt laadsnelheid als rankingfactor. Een snelle
                website scoort dus beter in de zoekresultaten. Optimaliseer je
                afbeeldingen, gebruik caching en kies voor betrouwbare hosting
                om je website snel te houden.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2759" data-mp="15wtufjmgzeik">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2760" data-mp="98e43lbjd14e">
                  💡 Tip: Test je website snelheid gratis via Google PageSpeed
                  Insights en verbeter de punten die het meeste impact hebben.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 2 — Mobiel */}
      <section className="py-24 bg-white" data-id="element-2761" data-mp="n5127rf85xbw">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2762" data-mp="1to93dv8r4u2s">
          <div className="flex items-start gap-6" data-id="element-2763" data-mp="1ymk9pvzu8mg9">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2764" data-mp="1yl9am9hc82vh">
              <SmartphoneIcon className="w-7 h-7 text-accent" data-id="element-2765" data-mp="18wkafni5zjzz" />
            </div>
            <div data-id="element-2766" data-mp="13yiql0cwzgng">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2767" data-mp="13z2b8oty8j56">
                Tip 2
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2768" data-mp="1dxrh1j8kmd52">
                Maak je website mobielvriendelijk
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2769" data-mp="q8fcwxs3ov9m">
                Meer dan 60% van alle websitebezoeken komt tegenwoordig via een
                smartphone. Als je website niet goed werkt op mobiel, verlies je
                het grootste deel van je potentiële klanten.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2770" data-mp="v5p8yw9zw56f">
                Een mobielvriendelijke website past zich automatisch aan het
                schermformaat aan. Knoppen zijn groot genoeg om op te tikken,
                tekst is leesbaar zonder te zoomen en formulieren zijn eenvoudig
                in te vullen op een klein scherm.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2771" data-mp="2ixg7bwzu9x4o">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2772" data-mp="248a13p2l36na">
                  💡 Tip: Bekijk je eigen website op je telefoon. Kun je alles
                  makkelijk lezen en aanklikken? Zo niet, dan is het tijd voor
                  een update.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 3 — SEO */}
      <section className="py-24 bg-surface-muted" data-id="element-2773" data-mp="2nw0zd94xtai1">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2774" data-mp="2nv82czvs5zd3">
          <div className="flex items-start gap-6" data-id="element-2775" data-mp="2rw1obnpnx43c">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2776" data-mp="2ryxoliozg4uy">
              <SearchIcon className="w-7 h-7 text-accent" data-id="element-2777" data-mp="1gho4qv1xzat4" />
            </div>
            <div data-id="element-2778" data-mp="1gftldl6bjqr5">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2779" data-mp="285hbjr9zoys9">
                Tip 3
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2780" data-mp="235cuo056621i">
                Investeer in SEO vanaf dag één
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2781" data-mp="1z6nkegffh0m3">
                Zoekmachineoptimalisatie (SEO) is geen luxe, maar een noodzaak.
                Als je website niet vindbaar is in Google, missen potentiële
                klanten je bedrijf. SEO zorgt ervoor dat je structureel gevonden
                wordt door mensen die actief zoeken naar jouw diensten.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2782" data-mp="2i1c0t58pxs40">
                Begin met de basis: gebruik relevante zoekwoorden in je
                paginatitels en teksten, schrijf unieke meta-beschrijvingen en
                zorg voor een logische sitestructuur. Technische SEO — zoals
                snelle laadtijden en een veilige verbinding (SSL) — is minstens
                zo belangrijk.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2783" data-mp="3i38pfx9jibm8">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2784" data-mp="3n2lcoj4bpkpc">
                  💡 Tip: Focus op lokale SEO als je een lokaal bedrijf hebt.
                  Vermeld je vestigingsplaats in titels en teksten, en claim je
                  Google Bedrijfsprofiel.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tip 4 — Vertrouwen */}
      <section className="py-24 bg-white" data-id="element-2785" data-mp="9np5xpyo87kd">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2786" data-mp="9p049a9ckn43">
          <div className="flex items-start gap-6" data-id="element-2787" data-mp="ena1dqemtxsc">
            <div className="w-14 h-14 rounded-xl bg-accent-light flex items-center justify-center flex-shrink-0" data-id="element-2788" data-mp="elywtodd4m06">
              <ShieldCheckIcon className="w-7 h-7 text-accent" data-id="element-2789" data-mp="imiuqu5rftnd" />
            </div>
            <div data-id="element-2790" data-mp="nnq42xnjx0w5">
              <span className="text-accent font-semibold text-sm uppercase tracking-wider" data-id="element-2791" data-mp="nl3vqvsu6hcv">
                Tip 4
              </span>
              <h2 className="text-2xl lg:text-3xl font-bold text-navy-900 mt-2 mb-4" data-id="element-2792" data-mp="1w2rlt9ww00eu">
                Bouw vertrouwen op met je website
              </h2>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2793" data-mp="210b4cm0mazcl">
                Bezoekers beslissen binnen enkele seconden of ze jouw website
                vertrouwen. Een professioneel design, duidelijke contactgegevens
                en sociale bewijskracht (reviews, logo's van klanten) maken het
                verschil.
              </p>
              <p className="text-slate-600 leading-relaxed mb-4" data-id="element-2794" data-mp="2jsm261gf073u">
                Zorg voor een duidelijke over-ons pagina, toon je telefoonnummer
                en adres zichtbaar en gebruik echte foto's in plaats van
                stockfoto's. Een SSL-certificaat (het groene slotje) is
                tegenwoordig een absolute must voor het vertrouwen van bezoekers
                én Google.
              </p>
              <div className="bg-white border border-navy-100 rounded-xl p-5 mt-6" data-id="element-2795" data-mp="37p0gmbpzc1pk">
                <p className="text-navy-700 text-sm font-medium italic" data-id="element-2796" data-mp="32wfr3me3waru">
                  💡 Tip: Vraag tevreden klanten om een review en plaats deze
                  zichtbaar op je website. Niets overtuigt beter dan de ervaring
                  van anderen.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gerelateerde artikelen */}
      <section className="py-24 bg-surface-muted" data-id="element-2797" data-mp="3mmj4zcyqwcwh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2798" data-mp="3mjn7tquq1s93">
          <div className="text-center mb-12" data-id="element-2799" data-mp="3hktqtog87j46">
            <h2 className="text-3xl lg:text-4xl font-bold text-navy-900 mb-4" data-id="element-2800" data-mp="3cnabel7sl0an">
              Gerelateerde artikelen
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto" data-id="element-2801" data-mp="26815owhxxsx3">
            <a href="#/kosten-website-laten-maken" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300" data-id="element-2802" data-mp="2173keb1mwird">
              <div className="aspect-[16/10] overflow-hidden" data-id="element-2803" data-mp="27n3txp8pxs9z">
                <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=600&h=400&fit=crop" alt="Kosten website laten maken" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2804" data-mp="1tp5f6iqhjtty" />
              </div>
              <div className="p-6" data-id="element-2805" data-mp="1oqknx8t7okhn">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2" data-id="element-2806" data-mp="1orm3nql69v5p">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-2807" data-mp="1azz5tmbl0ekt" />
                  15 feb 2026
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-accent transition-colors" data-id="element-2808" data-mp="1exn3uy8kmngx">
                  Kosten website laten maken
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2809" data-mp="15yi4xqt3ks48">
                  Wat kost een professionele website in 2026? Wij zetten de
                  prijzen op een rij.
                </p>
                <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm mt-3 group-hover:gap-2.5 transition-all" data-id="element-2810" data-mp="hfpvvy1nze0v">
                  Lees meer
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2811" data-mp="rc2vtdnsyo42" />
                </span>
              </div>
            </a>

            <a href="#/website-laten-maken-checklist" className="group bg-white rounded-2xl overflow-hidden shadow-soft hover:shadow-medium transition-all duration-300" data-id="element-2812" data-mp="15asw125x53wn">
              <div className="aspect-[16/10] overflow-hidden" data-id="element-2813" data-mp="1f1du8ohok6f1">
                <img src="https://images.unsplash.com/photo-1484480974693-6ca0a78fb36b?w=600&h=400&fit=crop" alt="Website laten maken checklist" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-2814" data-mp="1a2kcgjvd1t92" />
              </div>
              <div className="p-6" data-id="element-2815" data-mp="3dj7hmysv8nnr">
                <div className="flex items-center gap-2 text-xs text-slate-400 mb-2" data-id="element-2816" data-mp="3iituv88p23wk">
                  <CalendarIcon className="w-3.5 h-3.5" data-id="element-2817" data-mp="3igqxu494jaic" />8 feb 2026
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-2 group-hover:text-accent transition-colors" data-id="element-2818" data-mp="3nabt3ojc1qdu">
                  Website laten maken checklist
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed" data-id="element-2819" data-mp="2umfqk3731495">
                  Alles wat je moet weten voordat je een website laat maken.
                </p>
                <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm mt-3 group-hover:gap-2.5 transition-all" data-id="element-2820" data-mp="34isurv0970i5">
                  Lees meer
                  <ArrowRightIcon className="w-4 h-4" data-id="element-2821" data-mp="jstqbvif39ka" />
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde diensten */}
      <section className="py-24 bg-navy-900 relative overflow-hidden" data-id="element-2822" data-mp="nw9m6pmnx5vx">
        <div className="absolute inset-0 opacity-[0.03] pointer-events-none" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
        backgroundSize: '40px 40px'
      }} data-id="element-2823" data-mp="28bxsq46etq8j" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2824" data-mp="1ujzvorpz3ksq">
          <div className="text-center mb-12" data-id="element-2825" data-mp="1pmhigp4xajex">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4" data-id="element-2826" data-mp="1pn9eg12fmj5q">
              Gerelateerde diensten
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-2827" data-mp="1gl02d6rimhq8">
            <a href="#/website-laten-maken" className="group bg-white/[0.06] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.1] transition-all duration-300" data-id="element-2828" data-mp="1bm6qfirvmys6">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5" data-id="element-2829" data-mp="360hfnscdvuyh">
                <MonitorIcon className="w-6 h-6 text-accent" data-id="element-2830" data-mp="3p52ymjo0xoan" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-2831" data-mp="3tvjeeafbhm06">
                Website laten maken
              </h3>
              <p className="text-navy-300 text-sm mb-4" data-id="element-2832" data-mp="3g4odplez2b7o">
                Professionele websites die klanten opleveren. Van strategie tot
                oplevering.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-2833" data-mp="cnfwn3s2jfe0">
                Bekijk dienst
                <ArrowRightIcon className="w-4 h-4" data-id="element-2834" data-mp="go88m4sbrydz" />
              </span>
            </a>

            <a href="#/wordpress-websites" className="group bg-white/[0.06] border border-white/10 rounded-2xl p-8 hover:bg-white/[0.1] transition-all duration-300" data-id="element-2835" data-mp="2oqdl7ofrow6">
              <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center mb-5" data-id="element-2836" data-mp="2ne603qgmgrg0">
                <CodeIcon className="w-6 h-6 text-accent" data-id="element-2837" data-mp="2sfvbx8bo5955" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2" data-id="element-2838" data-mp="2ktqa26a33dz1">
                WordPress websites
              </h3>
              <p className="text-navy-300 text-sm mb-4" data-id="element-2839" data-mp="2fvokr3m2eif4">
                Flexibele WordPress sites met onbeperkte mogelijkheden en
                eenvoudig beheer.
              </p>
              <span className="inline-flex items-center gap-1.5 text-accent font-semibold text-sm group-hover:gap-2.5 transition-all" data-id="element-2840" data-mp="2ox62eqf4es5x">
                Bekijk dienst
                <ArrowRightIcon className="w-4 h-4" data-id="element-2841" data-mp="1x69nzldewfgj" />
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* Gerelateerde links strip */}
      <section className="py-12 bg-white border-b border-navy-100" data-id="element-2842" data-mp="2665484ltr0nf">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2843" data-mp="264u023ogvqvs">
          <div className="flex flex-wrap justify-center gap-4" data-id="element-2844" data-mp="1if9gbikhoplg">
            <a href="#/" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2845" data-mp="1dhr8jvi7kgf1">
              Homepage
            </a>
            <span className="text-navy-200 hidden sm:inline" data-id="element-2846" data-mp="wwzi8a99jumu">•</span>
            <a href="#/kennisbank" className="text-accent hover:text-accent-hover font-medium underline underline-offset-4" data-id="element-2847" data-mp="wzc0koixo70f">
              Kennisbank
            </a>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <FinalCtaSection data-id="element-2848" data-mp="1bu0dgnqny7q2" />
    </main>;
}